import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { Modal, Typography, Radio, Input } from 'antd'
import IntlMessages from '../../util/IntlMessages'
import { CancelReasonEnum } from '@vacationtracker/shared/types/billing'
import shuffle from 'lodash/shuffle'

const { Paragraph } = Typography
const { TextArea } = Input

// 'other' is rendered separately so it always stays last.
const REASON_KEYS = [
  'weSiwtchedToAnHrSolution',
  'anotherLeaveTrackingTool',
  'companyClosedOrDownsized',
  'priceOverBudget',
  'stoppedUsingIt',
  'missingFeatures',
  'builtOurOwnSolution',
] as const

type ReasonKey = (typeof REASON_KEYS)[number] | 'other'

interface ICancelSubscriptionModal {
  openCancelSubscriptionModal: boolean
  handleOnCancelSubscription: (reason: CancelReasonEnum, comment: string, integrationWithHr?: string) => Promise<void> | void
  closeModal: () => void
}

export const CancelSubscriptionModal = ({ openCancelSubscriptionModal, handleOnCancelSubscription, closeModal }: ICancelSubscriptionModal): React.ReactElement => {
  const { formatMessage } = useIntl()

  const [reasonKey, setReasonKey] = useState<ReasonKey | undefined>()
  const [cancelReasonComment, setCancelReasonComment] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [integrationWithHr, setIntegrationWithHr] = useState<string | undefined>()
  const [cancelReasons] = useState(() => shuffle([...REASON_KEYS]))

  const handleCancelReasonChange = (e) => {
    setReasonKey(e.target.value)
    setCancelReasonComment('')
    setIntegrationWithHr(undefined)
  }

  const isCancelButtonDisabled = () => {
    if (!reasonKey) {
      return true
    }
    if (cancelReasonComment.trim().length < (reasonKey === 'other' ? 20 : 1)) {
      return true
    }
    if (reasonKey === 'weSiwtchedToAnHrSolution' && !integrationWithHr) {
      return true
    }
    return false
  }

  return (
    <Modal
      title={<IntlMessages id="components.cancelSubscriptionModal.title" />}
      open={openCancelSubscriptionModal}
      onOk={() => {
        if (!reasonKey) return
        setIsLoading(true)
        handleOnCancelSubscription(CancelReasonEnum[reasonKey], cancelReasonComment, integrationWithHr)
      }}
      okButtonProps={{
        danger: true,
        disabled: isCancelButtonDisabled(),
      }}
      destroyOnHidden={true}
      okText={<IntlMessages id="components.cancelSubscriptionModal.okText" />}
      confirmLoading={isLoading}
      onCancel={() => closeModal()}
      cancelText={<IntlMessages id="components.cancelSubscriptionModal.cancelText" />}
    >
      <Paragraph>
        <IntlMessages id="components.cancelSubscriptionModal.info" />
      </Paragraph>
      <Radio.Group style={{ marginBottom: 20 }} onChange={handleCancelReasonChange} value={reasonKey}>
        {cancelReasons.map((cancelReason) => {
          return (
            <Radio key={cancelReason} style={{ display: 'block' }} value={cancelReason}>
              <IntlMessages id={`components.cancelSubscriptionModal.${cancelReason}`} />
            </Radio>
          )
        })}
        <Radio style={{ display: 'block' }} value="other">
          <IntlMessages id="components.cancelSubscriptionModal.other" />
        </Radio>
      </Radio.Group>
      {reasonKey && (
        <>
          <Paragraph style={{ marginBottom: 0 }}>
            <span style={{ color: 'red' }}>* </span>
            <IntlMessages id={`components.cancelSubscriptionModal.${reasonKey}AdditionalInfo`} />
          </Paragraph>
          <TextArea showCount rows={2} value={cancelReasonComment} onChange={(e) => setCancelReasonComment(e.target.value)} />
          {reasonKey === 'other' && (
            <Paragraph type="secondary" style={{ marginBottom: 10 }}>
              <IntlMessages id="components.cancelSubscriptionModal.min20characters" />
            </Paragraph>
          )}
          {reasonKey === 'weSiwtchedToAnHrSolution' && (
            <>
              <Paragraph style={{ marginBottom: 0, marginTop: 15 }}>
                <span style={{ color: 'red' }}>* </span>
                <IntlMessages id="components.cancelSubscriptionModal.weSiwtchedToAnHrSolutionAdditionalInfoIntegration" />
              </Paragraph>
              <Radio.Group onChange={(e) => setIntegrationWithHr(e.target.value)} value={integrationWithHr}>
                <Radio value="yes">
                  <IntlMessages id="app.yes" />{' '}
                </Radio>
                <Radio value="maybe">
                  <IntlMessages id="app.maybe" />{' '}
                </Radio>
                <Radio value="no">
                  <IntlMessages id="app.no" />{' '}
                </Radio>
              </Radio.Group>
            </>
          )}
        </>
      )}
      <Paragraph style={{ marginTop: 15 }}>{formatMessage({ id: 'components.cancelSubscriptionModal.contactInfo' }, { email: 'hello@vacationtracker.io' })}</Paragraph>
    </Modal>
  )
}

export default CancelSubscriptionModal
